import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import cancel from "../assets/images/navbar/cancel.png";
import group from "../assets/images/group/group.png";
import edit from "../assets/images/group/edit2.png";
import clock from "../assets/images/group/clock.png";
import creditcard from "../assets/images/support/credit-card.png";
import location from "../assets/images/location.png";
import rightarrow from "../assets/images/arrow-right.png";
import { useNavigate } from "react-router-dom";
import { addressBookService, AddressBookData, orderService } from "../services";

interface GroupModalProps {
  onClose: () => void;
  restaurantId: string;
}

const GroupModal = ({ onClose, restaurantId }: GroupModalProps) => {
  const navigate = useNavigate();
  const [groupName, setGroupName] = useState("My Group");
  const [editingName, setEditingName] = useState(false);
  const [addresses, setAddresses] = useState<AddressBookData[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string>("");
  const [creating, setCreating] = useState(false);
  const [loadingAddresses, setLoadingAddresses] = useState(true);

  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        setLoadingAddresses(true);
        const res: any = await addressBookService.getAllAddresses();
        if (res.status && res.data) {
          setAddresses(res.data);
          const defaultAddr =
            res.data.find((a: AddressBookData) => a.isDefault) || res.data[0];
          if (defaultAddr?._id) setSelectedAddressId(defaultAddr._id);
        }
      } catch (error) {
        console.error("Failed to load addresses:", error);
      } finally {
        setLoadingAddresses(false);
      }
    };
    fetchAddresses();
  }, []);

  const selectedAddress = addresses.find((a) => a._id === selectedAddressId);

  const handleCreateGroup = async () => {
    if (!selectedAddressId || !selectedAddress) {
      toast.error("Please select a delivery address");
      return;
    }
    if (!groupName.trim()) {
      toast.error("Please enter a group name");
      return;
    }
    if (!restaurantId) {
      toast.error("Restaurant not found");
      return;
    }

    try {
      setCreating(true);
      const response: any = await orderService.createGroupOrder({
        restaurantId,
        maxMembers: 49,
        deliveryAddress: {
          fullName: selectedAddress.fullName,
          phone: selectedAddress.phone,
          addressLine1: selectedAddress.addressLine1,
          addressLine2: selectedAddress.addressLine2,
          city: selectedAddress.city,
          pincode: selectedAddress.pincode,
          geoLocation: selectedAddress.geoLocation,
        },
        name: groupName.trim(),
      });

      if (response.status && response.data) {
        onClose();
        const sid =
          response.data.sessionId ||
          response.data.session?._id ||
          response.data._id;
        if (!sid) {
          toast.error("Group created but session id missing");
          return;
        }
        navigate(`/group-order/${sid}`);
      } else {
        toast.error(response.message || "Failed to create group order");
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to create group order");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 z-[1003] transition-colors duration-300"
        onClick={onClose}
      />

      <div className="fixed inset-0 flex items-center justify-center z-[1004] pointer-events-none">
        <div
          className="w-[545px] max-w-[95vw] max-h-[90vh] overflow-y-auto p-8 rounded-[24px] bg-white dark:bg-gray-800 pointer-events-auto relative transition-colors duration-300"
          style={{ boxShadow: "-23px 23px 72px 0 rgba(0, 0, 0, 0.15)" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="absolute top-6 right-6 h-[36px] w-[36px] cursor-pointer border dark:border-gray-600 rounded-full p-[7px] bg-white dark:bg-gray-700 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
            onClick={onClose}
          >
            <img src={cancel} alt="Close" className="h-[18px] w-[18px]" />
          </div>

          <div className="pb-[24px] border-b-2 dark:border-gray-700">
            <p className="text-[#050505] dark:text-white font-urbanist text-[20px] font-semibold leading-[140%]">
              Group Order
            </p>
          </div>

          <div className="flex flex-col justify-center items-center mt-[24px]">
            <img src={group} alt="Group" className="w-[370px] h-[212px] max-w-full" />
            <div className="flex gap-4 items-center mt-[24px]">
              {editingName ? (
                <input
                  autoFocus
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  onBlur={() => setEditingName(false)}
                  onKeyDown={(e) => e.key === "Enter" && setEditingName(false)}
                  className="text-[#050505] dark:text-white font-urbanist text-[22px] font-semibold bg-transparent border-b border-[#7402A7] outline-none text-center"
                />
              ) : (
                <h6 className="text-[#050505] dark:text-white font-urbanist text-[22px] font-semibold leading-[140%]">
                  {groupName}
                </h6>
              )}
              <img
                src={edit}
                alt="Edit"
                className="w-[20px] h-[20px] cursor-pointer"
                onClick={() => setEditingName(true)}
              />
            </div>
            <p className="text-[#050505] dark:text-gray-300 font-urbanist text-[16px] font-medium leading-[140%]">
              Invite your friends to add items in single cart
            </p>
          </div>

          <div className="border dark:border-gray-700 rounded-[16px] p-[12px] mt-4">
            <div className="flex gap-[12px] items-center pb-[12px] border-b-2 dark:border-gray-700">
              <div className="flex justify-center items-center h-[48px] w-[48px] rounded-[16px] border-2 dark:border-gray-600">
                <img src={clock} alt="clock" className="h-[24px] w-[24px]" />
              </div>
              <div>
                <h6 className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-[14px] font-medium leading-[140%]">
                  When to Deliver
                </h6>
                <p className="text-[#050505] dark:text-white font-urbanist text-[16px] font-normal leading-[160%]">
                  Deliver Now
                </p>
              </div>
            </div>

            <div className="py-[12px] border-b-2 dark:border-gray-700">
              <div className="flex gap-[12px] items-center mb-2">
                <div className="flex justify-center items-center h-[48px] w-[48px] rounded-[16px] border-2 dark:border-gray-600">
                  <img src={location} alt="location" className="h-[24px] w-[24px]" />
                </div>
                <div>
                  <h6 className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-[14px] font-medium leading-[140%]">
                    Where to Deliver
                  </h6>
                  {loadingAddresses ? (
                    <p className="text-sm text-gray-400">Loading addresses...</p>
                  ) : addresses.length === 0 ? (
                    <button
                      className="text-[#7402A7] text-sm"
                      onClick={() => {
                        onClose();
                        navigate("/saved-addresses");
                      }}
                    >
                      Add an address
                    </button>
                  ) : (
                    <select
                      value={selectedAddressId}
                      onChange={(e) => setSelectedAddressId(e.target.value)}
                      className="text-[#050505] dark:text-white dark:bg-gray-700 font-urbanist text-[14px] outline-none mt-1 max-w-[280px]"
                    >
                      {addresses.map((addr) => (
                        <option key={addr._id} value={addr._id}>
                          {addr.fullName} — {addr.addressLine1}, {addr.city}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-[12px] items-center mt-[12px]">
              <div className="flex justify-center items-center h-[48px] w-[48px] rounded-[16px] border-2 dark:border-gray-600">
                <img src={creditcard} alt="credit card" className="h-[24px] w-[24px]" />
              </div>
              <div>
                <h6 className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-[14px] font-medium leading-[140%]">
                  Who is Paying
                </h6>
                <p className="text-[#050505] dark:text-white font-urbanist text-[16px] font-normal leading-[160%]">
                  You are paying for this order
                </p>
              </div>
            </div>
          </div>

          <div className="h-[1px] w-full bg-[#E9E9E9] dark:bg-gray-700 my-[24px]" />

          <button
            className="w-full flex items-center justify-between py-4 px-6 bg-[#7402A7] dark:bg-purple-600 rounded-[150px] cursor-pointer hover:bg-[#5f0289] dark:hover:bg-purple-700 transition-colors disabled:opacity-50"
            onClick={handleCreateGroup}
            disabled={creating || loadingAddresses}
          >
            <span className="w-full text-white text-center font-urbanist text-[20px] font-semibold leading-[140%]">
              {creating ? "Creating..." : "Create Your Group"}
              <img
                src={rightarrow}
                alt="Right Arrow"
                className="inline-block ml-2 h-[24px] w-[24px]"
              />
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default GroupModal;
